import { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { supabase } from '../../config/supabase'
import { sendWelcomeEmail } from '../../services/emailService'
import { showAlert } from '../../services/dialogService'
import './Auth.css'

function Register() {
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [partnerFirstName, setPartnerFirstName] = useState('')
  const [partnerLastName, setPartnerLastName] = useState('')
  const [partnerEmail, setPartnerEmail] = useState('')
  const [weddingDate, setWeddingDate] = useState('')
  const [budget, setBudget] = useState('0.00')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const navigate = useNavigate()

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')

    // Validar campos requeridos
    if (!firstName || !lastName || !email || !password) {
      setError('Please fill in all required fields')
      return
    }

    // Validar longitud de contraseña
    if (password.length < 6) {
      setError('Password must be at least 6 characters long')
      return
    }

    setLoading(true)

    try {
      // Pasar los nombres en user_metadata para que estén disponibles inmediatamente
      const { data, error: signUpError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            first_name: firstName,
            last_name: lastName,
            full_name: `${firstName} ${lastName}`.trim(),
          }
        }
      })

      if (signUpError) {
        console.error('Error en signup:', signUpError)
        console.error('Código de error:', signUpError.code)
        console.error('Mensaje:', signUpError.message)
        
        // Proporcionar mensajes de error más específicos
        if (signUpError.message?.includes('already registered') || signUpError.message?.includes('already exists')) {
          throw new Error('This email is already registered. Please sign in or use a different email.')
        } else if (signUpError.message?.includes('password') || signUpError.message?.includes('6 characters')) {
          throw new Error('Password must be at least 6 characters long.')
        } else if (signUpError.message?.includes('email') || signUpError.message?.includes('invalid')) {
          throw new Error('Please enter a valid email address.')
        } else {
          throw signUpError
        }
      }

      if (data?.session && data?.user) {
        // Esperar un momento para que el trigger cree el perfil
        await new Promise(resolve => setTimeout(resolve, 500))
        
        // Actualizar el perfil con todos los datos
        // CRÍTICO: El usuario principal NUNCA debe tener partner_user_id
        const profileUpdates = {
          first_name: firstName,
          last_name: lastName,
          full_name: `${firstName} ${lastName}`.trim(),
          email: email, // Guardar el email del usuario principal
          partner_user_id: null, // CRÍTICO: Asegurar que el principal NO tiene partner_user_id
        }
        
        if (partnerFirstName) profileUpdates.partner_first_name = partnerFirstName
        if (partnerLastName) profileUpdates.partner_last_name = partnerLastName
        if (partnerEmail) profileUpdates.partner_email = partnerEmail
        if (weddingDate) profileUpdates.wedding_date = weddingDate
        if (budget && !isNaN(parseFloat(budget))) {
          profileUpdates.budget = parseFloat(budget)
        }

        // Intentar actualizar el perfil, si no existe esperar un poco más y reintentar
        let profileError = null
        let retries = 3
        while (retries > 0) {
          const { error } = await supabase
            .from('profiles')
            .update(profileUpdates)
            .eq('id', data.user.id)
          
          profileError = error
          
          if (!error) {
            break // Éxito, salir del loop
          }
          
          // Si el error es que no existe el perfil, esperar un poco más
          if (error.code === 'PGRST116' || error.message?.includes('No rows')) {
            await new Promise(resolve => setTimeout(resolve, 500))
            retries--
          } else {
            break // Otro tipo de error, salir
          }
        }

        if (profileError) {
          console.error('Error updating profile after retries:', profileError)
          // Intentar insertar si el update falló (por si el trigger no funcionó)
          const { error: insertError } = await supabase
            .from('profiles')
            .insert({
              id: data.user.id,
              ...profileUpdates,
              email: email,
            })
          
          if (insertError) {
            console.error('Error inserting profile:', insertError)
          }
        }

        // La boda se creará automáticamente por el trigger SQL create_wedding_for_new_profile
        // cuando se crea el perfil. Esperar y verificar que se creó correctamente
        await new Promise(resolve => setTimeout(resolve, 1000))
        
        // Verificar que la boda se creó correctamente
        let weddingCheck = null
        let weddingCheckError = null
        let weddingRetries = 3
        
        while (weddingRetries > 0) {
          const { data: weddingData, error: weddingError } = await supabase
            .from('weddings')
            .select('id, name, event_date')
            .eq('user_id', data.user.id)
            .maybeSingle()
          
          weddingCheck = weddingData
          weddingCheckError = weddingError
          
          if (weddingData) {
            console.log('✅ Boda creada automáticamente por trigger:', weddingData)
            break
          }
          
          if (error && error.code !== 'PGRST116') {
            console.warn('Error verificando boda:', error)
            break
          }
          
          // Si no existe, esperar un poco más y reintentar
          await new Promise(resolve => setTimeout(resolve, 500))
          weddingRetries--
        }
        
        // Si la boda no se creó, intentar crearla manualmente
        if (!weddingCheck) {
          console.warn('⚠️ La boda no se creó automáticamente. Intentando crear manualmente...')
          
          try {
            // Generar nombre de boda y slug
            const weddingName = `${firstName}${partnerFirstName ? ` & ${partnerFirstName}` : ''}'s Wedding`
            const rsvpSlug = `${firstName.toLowerCase().replace(/\s+/g, '-')}-${Date.now().toString(36)}`
            
            const { data: newWedding, error: createError } = await supabase
              .from('weddings')
              .insert({
                user_id: data.user.id,
                name: weddingName,
                event_date: weddingDate || null,
                rsvp_slug: rsvpSlug
              })
              .select()
              .single()
            
            if (createError) {
              console.error('Error creando boda manualmente:', createError)
              // Si falla por constraint único, intentar obtener la boda existente
              if (createError.code === '23505') {
                const { data: existingWedding } = await supabase
                  .from('weddings')
                  .select('id, name')
                  .eq('user_id', data.user.id)
                  .maybeSingle()
                
                if (existingWedding) {
                  console.log('✅ Boda encontrada después de error de constraint:', existingWedding)
                  weddingCheck = existingWedding
                }
              }
            } else if (newWedding) {
              console.log('✅ Boda creada manualmente:', newWedding)
              weddingCheck = newWedding
            }
          } catch (manualCreateError) {
            console.error('Error en creación manual de boda:', manualCreateError)
          }
        }

        // Email de bienvenida para el usuario principal
        // Esperar un momento para asegurar que la sesión esté completamente establecida
        await new Promise(resolve => setTimeout(resolve, 300))
        
        try {
          const fullName = `${firstName} ${lastName}`.trim()
          console.log('📧 Enviando email de bienvenida a:', email)
          
          // Verificar que hay una sesión activa antes de llamar a la función
          const { data: { session } } = await supabase.auth.getSession()
          if (!session) {
            console.warn('⚠️ No hay sesión activa, el email se enviará después del login')
          } else {
            const emailResult = await sendWelcomeEmail(email, fullName)
            if (!emailResult.success) {
              console.error('❌ Error enviando email de bienvenida:', emailResult.error)
              console.warn('⚠️ El registro fue exitoso pero el email no se pudo enviar.')
              
              // Verificar si es un error 401 (autenticación)
              if (emailResult.error?.status === 401 || emailResult.error?.message?.includes('401')) {
                console.warn('Error 401: La Edge Function puede requerir autenticación.')
                console.warn('Solución: Ve a Supabase Dashboard → Edge Functions → send-welcome-email')
                console.warn('y desactiva "Require authentication" si está activada.')
              } else {
                console.warn('Verifica:')
                console.warn('1. Edge Function "send-welcome-email" está desplegada')
                console.warn('2. Secret RESEND_API_KEY está configurado en Supabase')
                console.warn('3. Dominio está verificado en Resend')
                console.warn('4. Revisa la consola para más detalles del error')
              }
            } else {
              console.log('✅ Email de bienvenida enviado exitosamente a:', email)
            }
          }
        } catch (emailError) {
          console.error('❌ Error al enviar email de bienvenida:', emailError)
          console.error('Detalles completos:', emailError)
          // No bloquear el registro si falla el email
        }

        // Si hay email del partner, verificar si ya existe y enviarle email también
        if (partnerEmail) {
          try {
            // Esperar un momento para que el trigger enlace los perfiles automáticamente
            await new Promise(resolve => setTimeout(resolve, 1000))
            
            // Verificar si el partner ya existe (buscando por partner_email o partner_user_id)
            const { data: currentProfile } = await supabase
              .from('profiles')
              .select('partner_user_id, partner_email')
              .eq('id', data.user.id)
              .maybeSingle()

            // Si hay partner_user_id configurado (el trigger lo enlazó automáticamente)
            // o si el partner_email coincide con un usuario existente
            if (currentProfile?.partner_user_id) {
              try {
                // Obtener el perfil del partner para obtener su nombre
                const { data: partnerProfile } = await supabase
                  .from('profiles')
                  .select('first_name, last_name, email')
                  .eq('id', currentProfile.partner_user_id)
                  .maybeSingle()

                if (partnerProfile) {
                  const partnerFullName = `${partnerProfile.first_name || ''} ${partnerProfile.last_name || ''}`.trim() || 'Partner'
                  const partnerEmailToSend = partnerProfile.email || partnerEmail
                  
                  console.log('📧 Enviando email de bienvenida al partner existente:', partnerEmailToSend)
                  
                  const partnerEmailResult = await sendWelcomeEmail(
                    partnerEmailToSend,
                    partnerFullName
                  )
                  
                  if (!partnerEmailResult.success) {
                    console.error('❌ Error enviando email de bienvenida al partner existente:', partnerEmailResult.error)
                  } else {
                    console.log('✅ Email de bienvenida al partner existente enviado exitosamente')
                  }
                }
              } catch (partnerEmailError) {
                console.error('❌ Error al enviar email de bienvenida al partner existente:', partnerEmailError)
                // No bloquear el registro si falla el email
              }
            }
          } catch (partnerCheckError) {
            console.warn('⚠️ Error verificando partner existente:', partnerCheckError)
            // Continuar con el flujo normal
          }
        }

        // Si hay email del partner, crear cuenta del partner y enlazarla
        if (partnerEmail && partnerFirstName && partnerLastName) {
          // Usar la misma contraseña que el usuario principal para el partner
          // así el partner puede iniciar sesión sin depender del email automático
          const partnerPassword = password

          try {
            // Crear cuenta del partner con nombres en user_metadata
            const { data: partnerData, error: partnerSignUpError } = await supabase.auth.signUp({
              email: partnerEmail,
              password: partnerPassword,
              options: {
                data: {
                  is_partner: true,
                  main_user_id: data.user.id,
                  first_name: partnerFirstName,
                  last_name: partnerLastName,
                  full_name: `${partnerFirstName} ${partnerLastName}`.trim(),
                }
              }
            })

            if (partnerSignUpError) {
              console.error('Error creating partner account:', partnerSignUpError)
            } else if (partnerData?.user) {
              // CRÍTICO: Crear el perfil del partner DIRECTAMENTE con partner_user_id configurado
              // Esto evita que el trigger cree una boda para el partner
              // Esperar un momento para que el trigger básico cree el perfil (si no existe)
              await new Promise(resolve => setTimeout(resolve, 300))
              
              // Insertar o actualizar el perfil del partner con partner_user_id desde el inicio
              const { error: profileInsertError } = await supabase
                .from('profiles')
                .upsert({
                  id: partnerData.user.id,
                  user_id: partnerData.user.id,
                  email: partnerEmail,
                  first_name: partnerFirstName,
                  last_name: partnerLastName,
                  full_name: `${partnerFirstName} ${partnerLastName}`.trim(),
                  partner_user_id: data.user.id, // CRÍTICO: Enlazar al principal desde el inicio
                  budget: null, // El partner NO tiene presupuesto propio
                  wedding_date: weddingDate || null,
                }, {
                  onConflict: 'id',
                  ignoreDuplicates: false
                })
              
              if (profileInsertError) {
                console.error('Error creating partner profile with link:', profileInsertError)
                // Si falla el upsert, intentar update
                const { error: updateError } = await supabase
                  .from('profiles')
                  .update({ 
                    partner_user_id: data.user.id,
                    budget: null,
                    first_name: partnerFirstName,
                    last_name: partnerLastName,
                    full_name: `${partnerFirstName} ${partnerLastName}`.trim(),
                    email: partnerEmail,
                    wedding_date: weddingDate || null,
                  })
                  .eq('id', partnerData.user.id)
                
                if (updateError) {
                  console.error('Error updating partner profile:', updateError)
                }
              }
              
              // CRÍTICO: Eliminar cualquier boda que se haya creado para el partner
              // El partner NO debe tener boda propia, debe usar la del principal
              // Esperar un momento para que los triggers procesen
              await new Promise(resolve => setTimeout(resolve, 500))
              
              // Buscar y eliminar TODAS las bodas del partner
              const { data: partnerWeddings, error: partnerWeddingsError } = await supabase
                .from('weddings')
                .select('id')
                .eq('user_id', partnerData.user.id)
              
              if (partnerWeddingsError) {
                console.warn('Error checking partner weddings:', partnerWeddingsError)
              }
              
              if (partnerWeddings && partnerWeddings.length > 0) {
                console.log(`⚠️ Eliminando ${partnerWeddings.length} boda(s) del partner que se crearon antes del enlace`)
                
                // Obtener la boda del principal para mover datos si es necesario
                const { data: mainWedding } = await supabase
                  .from('weddings')
                  .select('id')
                  .eq('user_id', data.user.id)
                  .maybeSingle()
                
                // Mover todos los datos del partner a la boda del principal antes de eliminar
                if (mainWedding) {
                  for (const wedding of partnerWeddings) {
                    console.log(`Moviendo datos de la boda del partner ${wedding.id} a la boda del principal ${mainWedding.id}`)
                    
                    // Mover datos (el trigger debería hacerlo, pero lo hacemos como respaldo)
                    await supabase
                      .from('guests')
                      .update({ wedding_id: mainWedding.id })
                      .eq('wedding_id', wedding.id)
                    
                    await supabase
                      .from('tasks')
                      .update({ wedding_id: mainWedding.id })
                      .eq('wedding_id', wedding.id)
                    
                    await supabase
                      .from('budget_items')
                      .update({ wedding_id: mainWedding.id })
                      .eq('wedding_id', wedding.id)
                    
                    await supabase
                      .from('suppliers')
                      .update({ wedding_id: mainWedding.id })
                      .eq('wedding_id', wedding.id)
                    
                    await supabase
                      .from('timeline_events')
                      .update({ wedding_id: mainWedding.id })
                      .eq('wedding_id', wedding.id)
                    
                    await supabase
                      .from('tables')
                      .update({ wedding_id: mainWedding.id })
                      .eq('wedding_id', wedding.id)
                    
                    await supabase
                      .from('guest_forms')
                      .update({ wedding_id: mainWedding.id })
                      .eq('wedding_id', wedding.id)
                    
                    await supabase
                      .from('rsvp_invites')
                      .update({ wedding_id: mainWedding.id })
                      .eq('wedding_id', wedding.id)
                    
                    // Eliminar la boda del partner
                    const { error: deleteError } = await supabase
                      .from('weddings')
                      .delete()
                      .eq('id', wedding.id)
                    
                    if (deleteError) {
                      console.error('Error eliminando boda del partner:', deleteError)
                    } else {
                      console.log(`✅ Boda del partner ${wedding.id} eliminada, datos movidos a ${mainWedding.id}`)
                    }
                  }
                } else {
                  // Si el principal no tiene boda, solo eliminar las del partner
                  for (const wedding of partnerWeddings) {
                    const { error: deleteError } = await supabase
                      .from('weddings')
                      .delete()
                      .eq('id', wedding.id)
                    
                    if (deleteError) {
                      console.error('Error eliminando boda del partner:', deleteError)
                    }
                  }
                }
              } else {
                console.log('✅ Partner no tiene bodas propias (correcto)')
              }
              
              // Obtener la boda del usuario principal para usar sus datos
              const { data: mainWedding } = await supabase
                .from('weddings')
                .select('id, name, event_date')
                .eq('user_id', data.user.id)
                .maybeSingle()
              
              // Actualizar perfil del partner con todos los datos
              // CRÍTICO: El partner DEBE tener partner_user_id apuntando al principal
              // Y el principal NO debe tener partner_user_id
              // IMPORTANTE: El partner NO debe tener su propio presupuesto, usa el del principal
              const partnerProfileUpdates = {
                first_name: partnerFirstName,
                last_name: partnerLastName,
                full_name: `${partnerFirstName} ${partnerLastName}`.trim(),
                email: partnerEmail, // Guardar el email del partner
                partner_user_id: data.user.id, // CRÍTICO: Esto conecta al partner con el principal
                wedding_date: weddingDate || null,
                budget: null, // CRÍTICO: El partner NO tiene presupuesto propio, usa el del principal
              }
              
              // Asegurar que el principal NO tiene partner_user_id (es el principal)
              await supabase
                .from('profiles')
                .update({ partner_user_id: null })
                .eq('id', data.user.id)
              
              let partnerProfileError = null
              let partnerRetries = 3
              while (partnerRetries > 0) {
                const { error } = await supabase
                  .from('profiles')
                  .update(partnerProfileUpdates)
                  .eq('id', partnerData.user.id)
                
                partnerProfileError = error
                
                if (!error) {
                  break // Éxito
                }
                
                if (error.code === 'PGRST116' || error.message?.includes('No rows')) {
                  await new Promise(resolve => setTimeout(resolve, 500))
                  partnerRetries--
                } else {
                  break
                }
              }
              
              if (partnerProfileError) {
                console.error('Error updating partner profile after retries:', partnerProfileError)
                // Intentar insertar si el update falló
                const { error: partnerInsertError } = await supabase
                  .from('profiles')
                  .insert({
                    id: partnerData.user.id,
                    user_id: partnerData.user.id,
                    ...partnerProfileUpdates,
                    email: partnerEmail,
                  })
                
                if (partnerInsertError) {
                  console.error('Error inserting partner profile:', partnerInsertError)
                }
              }

              // CRÍTICO: Asegurar que el partner use la boda del principal
              // Esperar un momento para que el trigger procese el partner_user_id
              await new Promise(resolve => setTimeout(resolve, 1000))
              
              // Verificar y consolidar bodas si es necesario
              // Si el partner tiene su propia boda (creada antes del enlace), moverla al principal
              const { data: partnerWedding } = await supabase
                .from('weddings')
                .select('id')
                .eq('user_id', partnerData.user.id)
                .maybeSingle()
              
              if (partnerWedding && mainWedding) {
                // El partner tiene su propia boda Y el principal también tiene una
                // Mover todos los datos del partner a la boda del principal y eliminar la boda del partner
                console.log(`Consolidating weddings: moving data from partner wedding ${partnerWedding.id} to principal wedding ${mainWedding.id}`)
                
                // Mover guests, tasks, budget_items, etc. a la boda del principal
                // Nota: Esto debería hacerlo el trigger, pero lo hacemos manualmente como respaldo
                const { error: moveError } = await supabase.rpc('move_wedding_data', {
                  source_wedding_id: partnerWedding.id,
                  target_wedding_id: mainWedding.id
                }).catch(() => {
                  // Si la función RPC no existe, hacerlo manualmente
                  console.warn('RPC function move_wedding_data not found, trigger should handle this')
                })
                
                if (moveError) {
                  console.warn('Error moving wedding data (trigger should handle this):', moveError)
                }
                
                // Eliminar la boda del partner (el trigger también debería hacerlo)
                const { error: deleteError } = await supabase
                  .from('weddings')
                  .delete()
                  .eq('id', partnerWedding.id)
                
                if (deleteError) {
                  console.warn('Error deleting partner wedding (trigger should handle this):', deleteError)
                } else {
                  console.log('Partner wedding deleted, both users now share the principal wedding')
                }
              } else if (partnerWedding && !mainWedding) {
                // El partner tiene boda pero el principal no (caso raro)
                // Mover la boda del partner al principal
                console.log(`Moving partner wedding ${partnerWedding.id} to principal ${data.user.id}`)
                const { error: moveError } = await supabase
                  .from('weddings')
                  .update({ user_id: data.user.id })
                  .eq('id', partnerWedding.id)
                
                if (moveError) {
                  console.error('Error moving partner wedding to principal:', moveError)
                } else {
                  console.log('Partner wedding moved to principal successfully')
                }
              }
              
              // CRÍTICO: Sincronizar el presupuesto del principal al partner
              // Ambos usuarios deben tener el mismo presupuesto en su columna budget
              if (budget && !isNaN(parseFloat(budget))) {
                const budgetValue = parseFloat(budget)
                
                // Verificar que el principal tiene el presupuesto guardado
                const { data: principalBudgetCheck } = await supabase
                  .from('profiles')
                  .select('budget')
                  .eq('id', data.user.id)
                  .maybeSingle()
                
                // Sincronizar el presupuesto en el partner también
                const { error: partnerBudgetError } = await supabase
                  .from('profiles')
                  .update({ budget: budgetValue })
                  .eq('id', partnerData.user.id)
                
                if (partnerBudgetError) {
                  console.warn('Error syncing budget to partner:', partnerBudgetError)
                } else {
                  console.log('✅ Presupuesto sincronizado: Principal y Partner tienen', budgetValue)
                }
              } else {
                // Si no hay presupuesto, asegurar que ambos tienen null
                await supabase
                  .from('profiles')
                  .update({ budget: null })
                  .eq('id', partnerData.user.id)
              }
              
              // Verificación final: asegurar que ambos usuarios están conectados correctamente
              // Esperar un momento para que los triggers procesen todo
              await new Promise(resolve => setTimeout(resolve, 500))
              
              // Verificar que el principal NO tiene partner_user_id
              const { data: mainProfile } = await supabase
                .from('profiles')
                .select('partner_user_id')
                .eq('id', data.user.id)
                .maybeSingle()
              
              if (mainProfile?.partner_user_id) {
                console.warn('⚠️ El principal tiene partner_user_id configurado. Limpiando...')
                await supabase
                  .from('profiles')
                  .update({ partner_user_id: null })
                  .eq('id', data.user.id)
              }
              
              // Verificar que el partner SÍ tiene partner_user_id apuntando al principal
              const { data: partnerProfile } = await supabase
                .from('profiles')
                .select('partner_user_id')
                .eq('id', partnerData.user.id)
                .maybeSingle()
              
              if (!partnerProfile?.partner_user_id || partnerProfile.partner_user_id !== data.user.id) {
                console.warn('⚠️ El partner no está enlazado correctamente. Corrigiendo...')
                await supabase
                  .from('profiles')
                  .update({ partner_user_id: data.user.id })
                  .eq('id', partnerData.user.id)
              }
              
              // Verificar que ambos usuarios están conectados a la misma boda
              const { data: finalMainWedding } = await supabase
                .from('weddings')
                .select('id')
                .eq('user_id', data.user.id)
                .maybeSingle()
              
              const { data: finalPartnerWedding } = await supabase
                .from('weddings')
                .select('id')
                .eq('user_id', partnerData.user.id)
                .maybeSingle()
              
              if (finalMainWedding && finalPartnerWedding && finalMainWedding.id !== finalPartnerWedding.id) {
                console.error('⚠️ WARNING: Principal and partner have different weddings!')
                console.error(`Principal wedding: ${finalMainWedding.id}, Partner wedding: ${finalPartnerWedding.id}`)
                // Intentar una última vez mover la boda del partner al principal
                const { error: finalMoveError } = await supabase
                  .from('weddings')
                  .update({ user_id: data.user.id })
                  .eq('id', finalPartnerWedding.id)
                
                if (finalMoveError) {
                  console.error('Final attempt to consolidate weddings failed:', finalMoveError)
                } else {
                  // Eliminar la boda duplicada del partner
                  await supabase
                    .from('weddings')
                    .delete()
                    .eq('id', finalPartnerWedding.id)
                }
              } else if (finalMainWedding && !finalPartnerWedding) {
                console.log('✅ Success: Both users are connected to the same wedding')
              }
              
              // Verificación final: ambos perfiles están correctamente configurados
              const { data: finalMainProfile } = await supabase
                .from('profiles')
                .select('id, partner_user_id')
                .eq('id', data.user.id)
                .maybeSingle()
              
              const { data: finalPartnerProfile } = await supabase
                .from('profiles')
                .select('id, partner_user_id')
                .eq('id', partnerData.user.id)
                .maybeSingle()
              
              if (finalMainProfile && finalPartnerProfile) {
                const isCorrect = 
                  finalMainProfile.partner_user_id === null && 
                  finalPartnerProfile.partner_user_id === data.user.id
                
                if (isCorrect) {
                  console.log('✅ Verificación final: Ambos usuarios están correctamente enlazados')
                } else {
                  console.error('❌ Verificación final falló:')
                  console.error(`Principal partner_user_id: ${finalMainProfile.partner_user_id} (debe ser null)`)
                  console.error(`Partner partner_user_id: ${finalPartnerProfile.partner_user_id} (debe ser ${data.user.id})`)
                }
              }

              // Email de bienvenida para el partner
              // Esperar un momento para asegurar que la sesión esté completamente establecida
              await new Promise(resolve => setTimeout(resolve, 300))
              
              try {
                const partnerFullName = `${partnerFirstName} ${partnerLastName}`.trim()
                console.log('📧 Enviando email de bienvenida al partner:', partnerEmail)
                
                // Verificar que hay una sesión activa antes de llamar a la función
                const { data: { session: partnerSession } } = await supabase.auth.getSession()
                if (!partnerSession) {
                  console.warn('⚠️ No hay sesión activa para el partner, el email se enviará después del login')
                } else {
                  const partnerEmailResult = await sendWelcomeEmail(
                    partnerEmail,
                    partnerFullName,
                    {
                      includeTempPassword: false, // No incluir contraseña temporal porque usa la misma que el principal
                      tempPassword: null,
                    }
                  )
                  if (!partnerEmailResult.success) {
                    console.error('❌ Error enviando email de bienvenida al partner:', partnerEmailResult.error)
                    
                    // Verificar si es un error 401
                    if (partnerEmailResult.error?.status === 401 || partnerEmailResult.error?.message?.includes('401')) {
                      console.warn('Error 401: La Edge Function puede requerir autenticación.')
                    } else {
                      console.warn('⚠️ El registro del partner fue exitoso pero el email no se pudo enviar.')
                    }
                  } else {
                    console.log('✅ Email de bienvenida al partner enviado exitosamente')
                  }
                }
              } catch (partnerEmailError) {
                console.error('❌ Error al enviar email de bienvenida al partner:', partnerEmailError)
                // No bloquear el registro si falla el email
              }
            }
          } catch (partnerErr) {
            console.error('Error setting up partner account:', partnerErr)
          }
        }

        // Al finalizar el registro, NO mantener la sesión iniciada
        // Cerramos sesión y pedimos que el usuario inicie manualmente
        await supabase.auth.signOut()
        await showAlert('Your account has been created. Please log in with your email and password to start using your dashboard.')
        navigate('/login')
      } else {
        // Usuario necesita confirmar email (caso sin sesión automática)
        await supabase.auth.signOut()
        await showAlert('Please check your email to confirm your account, then log in.')
        navigate('/login')
      }
    } catch (err) {
      setError(err.message || 'Error registering. Please try again.')
      console.error('Register error:', err)
    } finally {
      setLoading(false)
    }
  }

  const formatDateForInput = (dateString) => {
    if (!dateString) return ''
    // Si viene en formato dd/mm/yyyy, convertir a yyyy-mm-dd
    if (dateString.includes('/')) {
      const parts = dateString.split('/')
      if (parts.length === 3) {
        return `${parts[2]}-${parts[1]}-${parts[0]}`
      }
    }
    return dateString
  }

  const handleDateChange = (e) => {
    const value = e.target.value
    setWeddingDate(value)
  }

  return (
    <div className="auth-page">
      <div className="auth-container">
        <p className="auth-welcome-text">
          Welcome to your calm corner of wedding planning. Take a breath, fill in your details, and start building your wedding dashboard — finally, a space that makes planning feel doable.
        </p>
        
        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="firstName">Name</label>
              <input
                type="text"
                id="firstName"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="lastName">Last Name</label>
              <input
                type="text"
                id="lastName"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="email">Email address</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="partnerFirstName">Partner Name</label>
              <input
                type="text"
                id="partnerFirstName"
                value={partnerFirstName}
                onChange={(e) => setPartnerFirstName(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label htmlFor="partnerLastName">Partner Last Name</label>
              <input
                type="text"
                id="partnerLastName"
                value={partnerLastName}
                onChange={(e) => setPartnerLastName(e.target.value)}
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="partnerEmail">Partner's email address</label>
            <input
              type="email"
              id="partnerEmail"
              value={partnerEmail}
              onChange={(e) => setPartnerEmail(e.target.value)}
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="weddingDate">Wedding Date</label>
              <input
                type="date"
                id="weddingDate"
                value={weddingDate}
                onChange={handleDateChange}
              />
            </div>
            <div className="form-group">
              <label htmlFor="budget">Budget</label>
              <input
                type="number"
                id="budget"
                value={budget}
                onChange={(e) => setBudget(e.target.value)}
                min="0"
                step="0.01"
                placeholder="0.00"
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <div className="password-input-wrapper">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? 'Hide' : 'Show'}
              </button>
            </div>
          </div>

          {error && (
            <div className="error-message">{error}</div>
          )}

          <button type="submit" className="btn-primary" disabled={loading}>
            {loading ? 'Registering...' : "Let's do this"}
          </button>

          <p className="auth-footer-text">
            Already have an account? <Link to="/login" className="auth-link">Sign In</Link>
          </p>
        </form>
      </div>
    </div>
  )
}

export default Register
